import * as React from 'react';
import ProfessionalDialog, { DialogActionButtons } from '@/components/common/professional-dialog';
import { Plus as PlusIcon } from '@phosphor-icons/react/dist/ssr/Plus';
import { BuildingOffice as BuildingOfficeIcon } from '@phosphor-icons/react/dist/ssr/BuildingOffice';
import { useQuery } from '@tanstack/react-query';

import type { Community } from '@/types/v2/community';
import type { UnitType } from '@/types/v2/unit-type';
import axios from '@/lib/v2/axios';

import { CreateOrEditCommunityForm } from './create-or-edit-community-form';

interface CreateOrEditCommunityModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (community: Community) => void;
  mode: 'create' | 'edit';
  initialData?: {};
  communityId?: string;
}

export function CreateOrEditCommunityModal({
  open,
  onClose,
  onSuccess,
  mode,
  initialData,
  communityId,
}: CreateOrEditCommunityModalProps): React.JSX.Element {
  const { data } = useQuery({
    // Use local UnitType
    queryKey: ['unit-types'],
    queryFn: async () => {
      const response = await axios.get('/api/v2/unit-types');
      return response.data;
    },
  });

  const handleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSuccess = React.useCallback(
    (community: Community) => {
      onSuccess(community);
      handleClose();
    },
    [onSuccess, handleClose]
  );

  return (
    <ProfessionalDialog
      actions={
        <DialogActionButtons
          cancelText="Cancel"
          confirmText={mode === 'create' ? 'Create Community' : 'Save Changes'}
          onCancel={handleClose}
          onConfirm={() => {
            // Submit via custom event from child form
            const form = document.getElementById('create-edit-community-form') as HTMLFormElement | null;
            form?.requestSubmit();
          }}
        />
      }
      icon={mode === 'create' ? <PlusIcon height={24} width={24} /> : <BuildingOfficeIcon height={24} width={24} />}
      maxWidth={mode === 'create' ? 'xs' : 'md'}
      onClose={handleClose}
      open={open}
      subtitle={
        mode === 'create'
          ? 'Creating a community helps organize units and tenants, streamlining maintenance request tracking.'
          : 'Editing a community allows for adjustments to unit and tenant organization, enhancing maintenance request management.'
      }
      title={mode === 'create' ? 'Create New Community' : 'Edit Community'}
      variant="modern"
    >
      <CreateOrEditCommunityForm
        communityId={communityId}
        initialData={initialData as Community}
        mode={mode}
        onSuccess={handleSuccess}
        unitTypes={data?.unitTypes as UnitType[]}
      />
    </ProfessionalDialog>
  );
}
